import React, {useEffect, useMemo, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Dropdown} from "primereact/dropdown";
import {Calendar} from "primereact/calendar";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {ProxyMonitorService} from "../../service/ProxyMonitorService";
import AdminPageHeader from "./AdminPageHeader";

// Scraper-side (business-perspective) efficiency of EVERY proxy provider — all
// traffic, not just the in-house unblocker: attempts, finalized requests, bans and
// retry recovery, deliberate skips and latency, per provider / host / customer over
// a date range (price.proxy_metrics via admin/proxy_metrics). The infra (per-IP)
// view lives in In-House Proxy Management -> Traffic; the A/B tool is the Canary
// screen. The eff_* strings live under the proxy_monitor locale namespace (this
// screen started life as a tab there).
const ProxyEfficiency = () => {

    const {t, setError} = React.useContext(AppContext);
    const service = new ProxyMonitorService();

    const [effRows, setEffRows] = useState([]);
    const [effSummary, setEffSummary] = useState(null);
    const [effDims, setEffDims] = useState({hosts: [], providers: [], users: []});
    const [effLoading, setEffLoading] = useState(false);
    // 1 | 3 | 7 | 30 | 90 | null = custom. Defaults to today: the screen is read to answer
    // "what is happening now", and the wider windows bury a provider that broke this
    // morning under a week of healthy traffic.
    const [effPreset, setEffPreset] = useState(1);
    const [effRange, setEffRange] = useState(null);         // [Date, Date] when custom
    const [effGroupBy, setEffGroupBy] = useState('provider');
    const [effCanary, setEffCanary] = useState('include');
    const [effFilters, setEffFilters] = useState({provider: null, host: null, user: null});
    const [effOutcome, setEffOutcome] = useState('all');  // client-side row filter, no refetch
    // Rate shortcuts, also client-side. Independent toggles that stack with each other and
    // with the outcome filter, so "banned AND erroring" is reachable.
    const [effHighBan, setEffHighBan] = useState(false);
    const [effHighError, setEffHighError] = useState(false);
    const [effExpandedRows, setEffExpandedRows] = useState(null);  // row-expansion detail panels
    const [effTruncated, setEffTruncated] = useState(false);

    // Global scraper config (price.scraper_config), reached from the header button and
    // edited in a dialog. It lives on this screen because the table below it is the
    // evidence for the decision: filter host=google.com, group by provider, compare, then
    // flip. `globalConfig[key]` is the server's view (value + whether it is stored or
    // still the scraper's own default - the search provider class's `proxy_default`, or
    // SearchProviderRegistry's DEFAULT_ENGINE); `configDraft[key]` is the pending
    // selection, so Save only lights up on a real change.
    const CONFIG_KEYS = ['search_engine', 'google_proxy', 'bing_proxy', 'yahoo_proxy', 'yandex_proxy'];
    const [globalConfig, setGlobalConfig] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [configDraft, setConfigDraft] = useState({});
    const [configSaving, setConfigSaving] = useState(false);

    useEffect(() => {
        Promise.all(CONFIG_KEYS.map(key => service.getScraperConfig(key)))
            .then(list => {
                const byKey = {};
                CONFIG_KEYS.forEach((key, index) => { byKey[key] = list[index]; });
                setGlobalConfig(byKey);
                setConfigDraft(Object.fromEntries(CONFIG_KEYS.map(k => [k, byKey[k].value])));
            })
            .catch(error => { setError(error); setError(null); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reopening after a cancelled edit must not resurrect the abandoned draft.
    const openConfig = () => {
        if (globalConfig) {
            setConfigDraft(Object.fromEntries(CONFIG_KEYS.map(k => [k, globalConfig[k].value])));
        }
        setShowConfig(true);
    };

    const changedKeys = () => (globalConfig
        ? CONFIG_KEYS.filter(key => configDraft[key] !== globalConfig[key].value) : []);

    // Save only the switches that actually changed: writing a key stamps updated_by, and
    // stamping a switch nobody touched would misattribute it.
    const saveConfig = () => {
        const keys = changedKeys();
        setConfigSaving(true);
        Promise.all(keys.map(key => service.setScraperConfig(key, configDraft[key])))
            .then(list => {
                const byKey = {...globalConfig};
                keys.forEach((key, index) => { byKey[key] = list[index]; });
                setGlobalConfig(byKey);
                setConfigDraft(Object.fromEntries(CONFIG_KEYS.map(k => [k, byKey[k].value])));
                setConfigSaving(false);
                setShowConfig(false);
            })
            .catch(error => {
                setError(error); setError(null);
                setConfigSaving(false);
            });
    };

    // Two date formats on purpose: docs are UTC-dated, so PRESET windows ("last N
    // days") anchor to UTC dates; the range Calendar instead keeps the user's picked
    // LOCAL calendar dates (toISOString there would shift a local midnight back a day
    // on a UTC+3 browser).
    const ymd = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
        + '-' + String(d.getDate()).padStart(2, '0');
    const ymdUtc = (d) => d.toISOString().slice(0, 10);
    const loadEfficiency = (over) => {
        const s = {preset: effPreset, range: effRange,
                   groupBy: effGroupBy, canary: effCanary, filters: effFilters, ...(over || {})};
        let from, to;
        if (s.preset) {
            to = ymdUtc(new Date());
            from = ymdUtc(new Date(Date.now() - (s.preset - 1) * 86400000));
        } else if (s.range && s.range[0] && s.range[1]) {
            from = ymd(s.range[0]);
            to = ymd(s.range[1]);
        } else return;  // custom picked but the range is not complete yet
        // The table shows range totals per group value, so a plain group_by keeps the
        // payload one row per value (the backend still supports day grouping and
        // week/month granularity for future use).
        const params = {from, to, group_by: s.groupBy, canary: s.canary};
        ['provider', 'host', 'user'].forEach(k => { if (s.filters[k]) params[k] = s.filters[k]; });
        setEffLoading(true);
        service.getEfficiency(params).then(data => {
            setEffRows((data && data.rows) || []);
            setEffSummary((data && data.summary) || null);
            setEffDims((data && data.dimensions) || {hosts: [], providers: [], users: []});
            setEffTruncated(!!(data && data.truncated));
            setEffLoading(false);
        }).catch(error => { setError(error); setError(null); setEffLoading(false); });
    };
    const effSet = (patch) => {
        // One setter per control + immediate reload with the fresh values.
        if ('preset' in patch) setEffPreset(patch.preset);
        if ('range' in patch) setEffRange(patch.range);
        if ('groupBy' in patch) setEffGroupBy(patch.groupBy);
        if ('canary' in patch) setEffCanary(patch.canary);
        if ('filters' in patch) setEffFilters(patch.filters);
        loadEfficiency(patch);
    };

    // One click for the question this screen exists to answer since the provider became a
    // panel switch: how do ScraperAPI and ZyteAPI compare on Google right now. Google
    // traffic is stamped as `google.com` (the scraper's _host strips the www.), and the
    // comparison only appears when the rows are grouped by provider, so the toggle sets
    // both. Toggling off clears the host filter and leaves the grouping alone.
    const GOOGLE_HOST = 'google.com';
    // Same number the summary tag already calls "danger", so a row the shortcut surfaces is
    // one the screen was already colouring red.
    const HIGH_RATE = 5;
    const googleOnly = effFilters.host === GOOGLE_HOST;
    const toggleGoogle = () => effSet(googleOnly
        ? {filters: {...effFilters, host: null}}
        : {filters: {...effFilters, host: GOOGLE_HOST}, groupBy: 'provider'});

    useEffect(() => {
        loadEfficiency();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Same thresholds as In-House Proxy Management's Traffic tab.
    const rateTag = (p) => {
        const sev = p >= 98 ? 'success' : (p >= 95 ? 'warning' : 'danger');
        return <Tag value={p.toFixed(1) + '%'} severity={sev}/>;
    };

    const EFF_COUNTERS = ['attempts', 'bans_seen', 'latency_ms_sum', 'latency_attempts',
                          'requests', 'success', 'ban_recovered', 'ban_final', 'retries',
                          'skipped', 'day_restriction', 'ignored',
                          'ok', 'no_data', 'ban', 'timeout', 'error'];
    // Table = the rows merged per group value (counters are additive). Memoized:
    // with host grouping over long ranges effRows can be large, and recomputing per
    // render froze the tab in its previous life.
    const effTableRows = useMemo(() => {
        const merged = {};
        effRows.forEach(r => {
            const key = r[effGroupBy] || '-';
            const target = merged[key] = merged[key] || {key, label: r.user_name || key};
            EFF_COUNTERS.forEach(c => { target[c] = (target[c] || 0) + (r[c] || 0); });
        });
        // Precompute the derived rates onto each merged row so their columns are
        // plain sortable fields; the column bodies only format them.
        return Object.values(merged).map(r => {
            const banDenominator = (r.ban_recovered || 0) + (r.ban_final || 0);
            r.success_rate = r.requests ? +(100 * (r.success || 0) / r.requests).toFixed(1) : null;
            r.ban_rate = r.requests ? +(100 * (r.ban_final || 0) / r.requests).toFixed(1) : null;
            r.error_rate = r.requests ? +(100 * (r.error || 0) / r.requests).toFixed(1) : null;
            r.recovery_rate = banDenominator ? +(100 * (r.ban_recovered || 0) / banDenominator).toFixed(1) : null;
            r.avg_latency_ms = r.latency_attempts ? Math.round((r.latency_ms_sum || 0) / r.latency_attempts) : null;
            return r;
        }).sort((a, b) => (b.attempts || 0) - (a.attempts || 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effRows, effGroupBy]);
    const effRecoveryCol = (r) => r.recovery_rate != null ? r.recovery_rate + '%' : '-';
    const effSuccessCol = (r) => r.success_rate != null ? rateTag(r.success_rate) : <span>-</span>;
    const effBanRateCol = (r) => r.ban_rate != null ? <span>{r.ban_rate}%</span> : <span>-</span>;
    const effLatencyCol = (r) => r.avg_latency_ms != null ? (r.avg_latency_ms / 1000).toFixed(1) + ' s' : '-';
    // Drill-down chain provider -> host -> customer: clicking a provider row narrows
    // to that provider's per-host breakdown, clicking a host row narrows to that
    // host's per-customer breakdown (filter + regroup). Customer is the end of the
    // chain. Works for the synthetic rows (Skipped, NoProxy, Unknown) too; the
    // filter bar's clear buttons are the way back up.
    const effDrill = (r) => {
        if (effGroupBy === 'provider') return {hint: 'eff_drill_hint', patch: {filters: {...effFilters, provider: r.key}, groupBy: 'host'}};
        if (effGroupBy === 'host') return {hint: 'eff_drill_host_hint', patch: {filters: {...effFilters, host: r.key}, groupBy: 'user'}};
        return null;
    };
    const effLabelCol = (r) => {
        const drill = effDrill(r);
        return drill
            ? <span style={{cursor: 'pointer', textDecoration: 'underline'}}
                    title={t('proxy_monitor.' + drill.hint)}
                    onClick={() => effSet(drill.patch)}>{r.label}</span>
            : <span>{r.label}</span>;
    };
    // Outcome quick-filter over the merged rows (client-side; the KPI summary stays
    // range-wide). "success" means a row with successes and NO ban/error/timeout.
    const effOutcomeOptions = [{label: t('proxy_monitor.eff_outcome_all'), value: 'all'},
                               {label: t('proxy_monitor.eff_outcome_ban'), value: 'ban'},
                               {label: t('proxy_monitor.eff_outcome_error'), value: 'error'},
                               {label: t('proxy_monitor.eff_outcome_success'), value: 'success'}];
    const effVisibleRows = effTableRows.filter(r => {
        // A null rate means the row finalized nothing, so it carries no evidence either
        // way and is dropped by both shortcuts (null >= HIGH_RATE is false).
        if (effHighBan && !(r.ban_rate >= HIGH_RATE)) return false;
        if (effHighError && !(r.error_rate >= HIGH_RATE)) return false;
        if (effOutcome === 'ban') return (r.bans_seen || 0) > 0 || (r.ban_final || 0) > 0;
        if (effOutcome === 'error') return (r.error || 0) > 0 || (r.timeout || 0) > 0;
        if (effOutcome === 'success') return (r.success || 0) > 0 &&
            !((r.bans_seen || 0) || (r.ban_final || 0) || (r.error || 0) || (r.timeout || 0));
        return true;
    });
    // Row-expansion detail: everything the summary columns leave out.
    const effDetail = (r) => {
        const stat = (labelKey, value) => (
            <div key={labelKey} style={{minWidth: '130px'}}>
                <div style={{fontSize: '0.78em', color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.' + labelKey)}</div>
                <div style={{fontWeight: 600}}>{(value || 0).toLocaleString()}</div>
            </div>
        );
        return <div style={{display: 'flex', flexWrap: 'wrap', gap: '1em 2em', padding: '0.75em 1em'}}>
            {stat('eff_day_restriction', r.day_restriction)}
            {stat('eff_ignored', r.ignored)}
            {stat('eff_no_data', r.no_data)}
            {stat('eff_bans_seen', r.bans_seen)}
            {stat('eff_ban_recovered', r.ban_recovered)}
            {stat('eff_ban_final', r.ban_final)}
            {stat('timeouts', r.timeout)}
            {stat('errors', r.error)}
            {stat('eff_retries', r.retries)}
        </div>;
    };
    // Value 1 spans the current UTC day only, not a rolling 24h window, hence its own
    // label ("Today") rather than the last_1 ("Last 24h") used by the Traffic tab.
    const effPresetOptions = [{label: t('proxy_monitor.eff_last_1'), value: 1},
                              {label: t('proxy_monitor.last_3'), value: 3},
                              {label: t('proxy_monitor.last_7'), value: 7},
                              {label: t('proxy_monitor.last_30'), value: 30},
                              {label: t('proxy_monitor.eff_last_90'), value: 90},
                              {label: t('proxy_monitor.eff_custom'), value: null}];
    const effGroupOptions = [{label: t('proxy_monitor.eff_provider'), value: 'provider'},
                             {label: t('proxy_monitor.host'), value: 'host'},
                             {label: t('proxy_monitor.eff_user'), value: 'user'}];
    const effCanaryOptions = [{label: t('proxy_monitor.eff_canary_include'), value: 'include'},
                              {label: t('proxy_monitor.eff_canary_exclude'), value: 'exclude'},
                              {label: t('proxy_monitor.eff_canary_only'), value: 'only'}];
    const effDim = (key, options) => (
        <Dropdown value={effFilters[key]} options={options} showClear={true} filter={true}
                  placeholder={t('proxy_monitor.all')} style={{width: '100%'}}
                  onChange={(e) => effSet({filters: {...effFilters, [key]: e.value}})}/>
    );
    const effSummaryTags = effSummary && (() => {
        const s = effSummary;
        return <div style={{display: 'flex', gap: '0.6em', alignItems: 'center', margin: '0.5em 0 1.5em', flexWrap: 'wrap'}}>
            <Tag value={t('proxy_monitor.eff_attempts') + ': ' + (s.attempts || 0).toLocaleString()}/>
            <Tag value={t('proxy_monitor.eff_requests') + ': ' + (s.requests || 0).toLocaleString()}/>
            {(s.skipped || 0) > 0 &&
                <Tag value={t('proxy_monitor.eff_skipped') + ': ' + s.skipped.toLocaleString()}/>}
            {s.success_rate != null &&
                <Tag value={t('proxy_monitor.success_rate') + ': ' + s.success_rate + '%'}
                     severity={s.success_rate >= 98 ? 'success' : (s.success_rate >= 95 ? 'warning' : 'danger')}/>}
            {s.ban_rate != null &&
                <Tag value={t('proxy_monitor.ban_rate') + ': ' + s.ban_rate + '%'}
                     severity={s.ban_rate >= 5 ? 'danger' : (s.ban_rate >= 1 ? 'warning' : 'success')}/>}
            <Tag value={t('proxy_monitor.eff_bans_seen') + ': ' + (s.bans_seen || 0).toLocaleString()}
                 severity={(s.bans_seen || 0) > 0 ? 'warning' : 'success'}/>
            {s.recovery_rate != null &&
                <Tag value={t('proxy_monitor.eff_recovery_rate') + ': ' + s.recovery_rate + '%'}/>}
            {s.avg_latency_ms != null &&
                <Tag value={t('proxy_monitor.avg_latency') + ': ' + (s.avg_latency_ms / 1000).toFixed(1) + ' s'}/>}
            {effTruncated &&
                <Tag severity="warning" value={t('proxy_monitor.eff_truncated')}/>}
        </div>;
    })();

    return <React.Fragment>
        <div className="card management-page">
            <AdminPageHeader
                title={t('menu_item.proxy_efficiency')}
                subtitle={t('menu.management_hub.page.proxy_efficiency')}
                actions={globalConfig &&
                    <Button label={t('proxy_monitor.global_config')} icon="pi pi-cog"
                            className="p-button-outlined" onClick={openConfig}/>}
            />

            <Dialog header={t('proxy_monitor.global_config')} visible={showConfig}
                    style={{width: '40vw', maxWidth: '520px', minWidth: '28vw'}}
                    breakpoints={{'960px': '85vw', '640px': '95vw'}}
                    modal={true} onHide={() => setShowConfig(false)}
                    footer={<span>
                        <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-text"
                                onClick={() => setShowConfig(false)}/>
                        <Button label={t('actions.save')} icon="pi pi-check" className="p-button-success"
                                loading={configSaving} onClick={saveConfig}
                                disabled={configSaving || !globalConfig || changedKeys().length === 0}/>
                    </span>}>
                {globalConfig &&
                    <div style={{display: 'flex', flexDirection: 'column', gap: '1.2em'}}>
                        {CONFIG_KEYS.map(key => (
                            <div key={key} style={{display: 'flex', flexDirection: 'column', gap: '0.5em'}}>
                                <label>{t(`proxy_monitor.${key}`)}</label>
                                <Dropdown value={configDraft[key]} style={{width: '100%'}}
                                          options={(globalConfig[key].options || []).map(o => ({label: o, value: o}))}
                                          onChange={(e) => setConfigDraft({...configDraft, [key]: e.value})}/>
                                <span style={{color: 'var(--text-color-secondary)', fontSize: '0.85em'}}>
                                    {t(`proxy_monitor.${key}_hint`)}</span>
                                {globalConfig[key].stored == null &&
                                    <span style={{color: 'var(--text-color-secondary)', fontSize: '0.85em'}}>
                                        {t('proxy_monitor.config_unset', {value: globalConfig[key].default})}</span>}
                                {globalConfig[key].updated_by &&
                                    <span style={{color: 'var(--text-color-secondary)', fontSize: '0.85em'}}>
                                        {t('proxy_monitor.config_updated', {user: globalConfig[key].updated_by})}</span>}
                            </div>
                        ))}
                    </div>}
            </Dialog>

            {/* One flex row: seven filters share the width evenly; narrow screens wrap. */}
            <div className="p-fluid" style={{display: 'flex', gap: '0.75em', flexWrap: 'wrap',
                                             alignItems: 'flex-end', marginBottom: '1em'}}>
                <div style={{flex: '1 1 130px', minWidth: '120px'}}><label>{t('proxy_monitor.range')}</label>
                    <Dropdown value={effPreset} options={effPresetOptions} style={{width: '100%'}}
                              onChange={(e) => effSet({preset: e.value})}/></div>
                {effPreset === null &&
                    <div style={{flex: '1 1 190px', minWidth: '180px'}}><label>{t('proxy_monitor.eff_custom')}</label>
                        <Calendar value={effRange} selectionMode="range" dateFormat="yy-mm-dd"
                                  maxDate={new Date()} readOnlyInput={true} style={{width: '100%'}}
                                  onChange={(e) => effSet({range: e.value})}/></div>}
                <div style={{flex: '1 1 130px', minWidth: '120px'}}><label>{t('proxy_monitor.group_by')}</label>
                    <Dropdown value={effGroupBy} options={effGroupOptions} style={{width: '100%'}}
                              onChange={(e) => effSet({groupBy: e.value})}/></div>
                <div style={{flex: '1 1 150px', minWidth: '130px'}}><label>{t('proxy_monitor.eff_provider')}</label>
                    {effDim('provider', (effDims.providers || []).map(p => ({label: p, value: p})))}</div>
                <div style={{flex: '1 1 150px', minWidth: '130px'}}><label>{t('proxy_monitor.host')}</label>
                    {effDim('host', (effDims.hosts || []).map(h => ({label: h, value: h})))}</div>
                <div style={{flex: '1 1 150px', minWidth: '130px'}}><label>{t('proxy_monitor.eff_user')}</label>
                    {effDim('user', (effDims.users || []).map(u => ({label: u.name, value: u.id})))}</div>
                <div style={{flex: '1 1 130px', minWidth: '120px'}}><label>{t('proxy_monitor.eff_outcome')}</label>
                    <Dropdown value={effOutcome} options={effOutcomeOptions} style={{width: '100%'}}
                              onChange={(e) => setEffOutcome(e.value)}/></div>
                <div style={{flex: '1 1 140px', minWidth: '125px'}}><label>{t('proxy_monitor.eff_canary')}</label>
                    <Dropdown value={effCanary} options={effCanaryOptions} style={{width: '100%'}}
                              onChange={(e) => effSet({canary: e.value})}/></div>
            </div>

            {/* Shortcuts sit OUTSIDE the p-fluid block above: p-fluid forces .p-button to
                width:100%, which would stretch each one across the page. */}
            <div style={{display: 'flex', gap: '0.5em', flexWrap: 'wrap', marginBottom: '1em'}}>
                <Button label={t('proxy_monitor.eff_google_only')} icon="pi pi-google"
                        className={'p-button-sm ' + (googleOnly ? 'p-button-info' : 'p-button-outlined')}
                        onClick={toggleGoogle}/>
                <Button label={t('proxy_monitor.eff_high_ban', {pct: HIGH_RATE})} icon="pi pi-ban"
                        className={'p-button-sm ' + (effHighBan ? 'p-button-danger' : 'p-button-outlined')}
                        onClick={() => setEffHighBan(!effHighBan)}/>
                <Button label={t('proxy_monitor.eff_high_error', {pct: HIGH_RATE})} icon="pi pi-exclamation-triangle"
                        className={'p-button-sm ' + (effHighError ? 'p-button-warning' : 'p-button-outlined')}
                        onClick={() => setEffHighError(!effHighError)}/>
            </div>

            {effSummaryTags}

            {effLoading ?
                <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div> :
                <DataTable value={effVisibleRows} emptyMessage={t('proxy_monitor.eff_no_rows')}
                           dataKey="key" paginator={effVisibleRows.length > 50} rows={50}
                           expandedRows={effExpandedRows} onRowToggle={(e) => setEffExpandedRows(e.data)}
                           rowExpansionTemplate={effDetail}>
                    <Column expander={true} headerStyle={{width: '3em'}}/>
                    <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                    <Column field="label" body={effLabelCol} header={t('proxy_monitor.' + (effGroupBy === 'provider' ? 'eff_provider' : (effGroupBy === 'user' ? 'eff_user' : 'host')))} sortable={true}/>
                    <Column field="attempts" header={t('proxy_monitor.eff_attempts')} sortable={true}
                            body={(r) => (r.attempts || 0).toLocaleString()}/>
                    <Column field="requests" sortable={true}
                            header={<span title={t('proxy_monitor.eff_requests_hint')}>{t('proxy_monitor.eff_requests')}</span>}
                            body={(r) => (r.requests || 0).toLocaleString()}/>
                    <Column field="success_rate" header={t('proxy_monitor.success_rate')} body={effSuccessCol} sortable={true}/>
                    <Column field="ban_rate" header={t('proxy_monitor.ban_rate')} body={effBanRateCol} sortable={true}/>
                    <Column field="recovery_rate" header={t('proxy_monitor.eff_recovery_rate')} body={effRecoveryCol} sortable={true}/>
                    <Column field="error_rate" header={t('proxy_monitor.eff_error_rate')} sortable={true}
                            body={(r) => r.error_rate != null ? r.error_rate + '%' : '-'}/>
                    <Column field="avg_latency_ms" header={t('proxy_monitor.avg_latency')} body={effLatencyCol} sortable={true}/>
                </DataTable>}
        </div>
    </React.Fragment>;
};
export default ProxyEfficiency;
